import { useState } from "react";
import { Divider, Form, Input, Modal, Popover, Radio, Space, Typography, message } from "antd";
import { EditOutlined, LogoutOutlined, SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";
import { authService } from "@/services/auth";
import { userService } from "@/services/user";
import { setLanguage } from "@/i18n";
import { UserAvatar } from "@/components/UserAvatar";

const { Text } = Typography;

interface UserCardProps {
  children: React.ReactNode;
}

export function UserCard({ children }: UserCardProps) {
  const { t, i18n } = useTranslation();
  const { user, setUser, logout } = useAuthStore();
  const { mode, setMode } = useThemeStore();
  const [profileOpen, setProfileOpen] = useState(false);
  const [prefOpen, setPrefOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [profileForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleLogout = async () => {
    try { await authService.logout(); } catch { /* ignore */ }
    logout();
    window.location.href = "/login";
  };

  const openProfile = () => {
    setPopoverOpen(false);
    if (user) profileForm.setFieldsValue({ name: user.name, avatar: user.avatar || "" });
    setProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    const values = await profileForm.validateFields();
    setSaving(true);
    try {
      const updated = await userService.updateMe({
        name: values.name,
        avatar: values.avatar || undefined,
      });
      setUser(updated);
      setProfileOpen(false);
      message.success(t("success"));
    } catch { message.error(t("error")); }
    finally { setSaving(false); }
  };

  const openPreferences = () => {
    setPopoverOpen(false);
    setPrefOpen(true);
  };

  if (!user) return <>{children}</>;

  const content = (
    <div style={{ width: 200 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 0 8px" }}>
        <UserAvatar name={user.name} avatar={user.avatar} size={40} />
        <div>
          <Text strong style={{ display: "block" }}>{user.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{user.account}</Text>
        </div>
      </div>
      <Divider style={{ margin: "8px 0" }} />
      <Space direction="vertical" style={{ width: "100%" }} size={0}>
        <div style={itemStyle} onClick={openProfile}>
          <EditOutlined /> <span>{t("user.profile")}</span>
        </div>
        <div style={itemStyle} onClick={openPreferences}>
          <SettingOutlined /> <span>{t("user.preferences")}</span>
        </div>
      </Space>
      <Divider style={{ margin: "8px 0" }} />
      <div style={{ ...itemStyle, color: "var(--error, #FF4D4F)" }} onClick={handleLogout}>
        <LogoutOutlined /> <span>{t("user.logout")}</span>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        placement="right"
        arrow={false}
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        overlayStyle={{ position: "fixed" }}
      >
        {children}
      </Popover>

      <Modal
        title={t("user.profile")}
        open={profileOpen}
        onOk={handleSaveProfile}
        onCancel={() => setProfileOpen(false)}
        confirmLoading={saving}
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item label={t("login.register.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Avatar URL" name="avatar">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("user.preferences")}
        open={prefOpen}
        onCancel={() => setPrefOpen(false)}
        footer={null}
      >
        <Form layout="vertical">
          <Form.Item label="Theme">
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              optionType="button"
              options={[
                { label: "System", value: "system" },
                { label: "Light", value: "light" },
                { label: "Dark", value: "dark" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Language">
            <Radio.Group
              value={i18n.language}
              onChange={(e) => setLanguage(e.target.value)}
              optionType="button"
              options={[
                { label: "中文", value: "zh-CN" },
                { label: "English", value: "en-US" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

const itemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 4px",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 14,
};
